import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { ChevronRight, Clock, Plus, ShoppingBag, TriangleAlert, Utensils } from "lucide-react"

import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { FilterChip } from "@/components/ui/filter-chip"
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import type { CancellationReasonOption } from "@/features/staff/cancellations"
import { type KanbanOrder, OrderDetailDrawer } from "@/features/staff/order-detail-drawer"
import {
  filterBoardOrders,
  groupOrdersByStatus,
  orderAgeMinutes,
  orderUrgency,
  primaryActionFor,
  sortOldestFirst,
  stationOptionsFrom,
  type OrderFlowAction,
  type Urgency,
} from "@/features/staff/order-flow"
import { orderTone, toneDotClass, toneTextClass } from "@/lib/status-colors"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { cn } from "@/lib/utils"

const I18N_BASE = "ui.react.features.staff.orders_board"

// An elapsed figure that never moves is a lie the moment the shift gets busy.
const TICK_INTERVAL_MS = 30_000

const ALL_STATIONS = "all"

type StatusColumn = {
  value: string
  label: string
}

type Props = {
  orders: KanbanOrder[]
  statusColumns: StatusColumn[]
  /** The closed cancellation catalog, straight from `Location::CancellationPolicy.options`. */
  cancellationReasons?: CancellationReasonOption[]
}

export function OrdersBoard({ orders, statusColumns, cancellationReasons = [] }: Props) {
  const [now, setNow] = useState(() => new Date())
  const [station, setStation] = useState(ALL_STATIONS)
  const [onlyDelayed, setOnlyDelayed] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [advancingOrderId, setAdvancingOrderId] = useState<number | null>(null)
  const [serviceTypeOpen, setServiceTypeOpen] = useState(false)

  // Where the narrow layout is parked. Seeded from the first render — a column
  // that empties while someone is working in it should not teleport them
  // elsewhere mid-shift.
  const [visibleStatus, setVisibleStatus] = useState(() => {
    const firstBusy = statusColumns.find((column) =>
      orders.some((order) => order.status === column.value)
    )
    return firstBusy?.value ?? statusColumns[0]?.value ?? ""
  })

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), TICK_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [])

  const stationOptions = useMemo(() => stationOptionsFrom(orders), [orders])

  const visible = useMemo(
    () => sortOldestFirst(filterBoardOrders(orders, { station, onlyDelayed, now })),
    [orders, station, onlyDelayed, now]
  )

  const columnValues = useMemo(() => statusColumns.map((column) => column.value), [statusColumns])
  const grouped = useMemo(() => groupOrdersByStatus(visible, columnValues), [visible, columnValues])

  const delayedCount = useMemo(
    () =>
      orders.filter(
        (order) => orderUrgency(order.status, orderAgeMinutes(order.placedAt, now)) !== "normal"
      ).length,
    [orders, now]
  )

  const selectedOrder =
    selectedOrderId === null ? null : orders.find((order) => order.id === selectedOrderId) ?? null

  const handleAdvance = (order: KanbanOrder, action: OrderFlowAction) => {
    if (advancingOrderId !== null) return

    // Cancelling carries a mandatory reason (`Location::CancellationPolicy`),
    // which a card's single quick-action button has nowhere to collect. The
    // drawer is the one surface built to ask for it, so hand off to it rather
    // than posting a status change the server will refuse for lacking one.
    if (action.status === "cancelled") {
      setSelectedOrderId(order.id)
      return
    }

    setAdvancingOrderId(order.id)
    patchInline(
      `/orders/${order.id}`,
      { order: { status: action.status } },
      // The board holds its filters and its narrow-layout column in local state;
      // without this the redirect-reload throws all of it away on every tap.
      { preserveState: true }
    )
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setAdvancingOrderId(null))
  }

  const hasOrders = orders.length > 0
  const hasVisibleOrders = visible.length > 0

  return (
    <MainContainer size="md">
      <div className="space-y-4 pb-6" data-testid="orders-kanban-page">
        <PageHeader
          title={t(`${I18N_BASE}.title`)}
          description={t(`${I18N_BASE}.description`)}
          actions={
            <Button type="button" onClick={() => setServiceTypeOpen(true)} data-testid="orders-new-order-button">
              <Plus className="size-4" aria-hidden="true" />
              {t(`${I18N_BASE}.new_order`)}
            </Button>
          }
        />

        {hasOrders ? (
          <>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {/* One station is no choice at all, so the row only appears when
                    the venue actually splits work across stations. */}
                {stationOptions.length > 1 ? (
                  <>
                    <div
                      className="flex flex-wrap items-center gap-2"
                      role="group"
                      aria-label={t(`${I18N_BASE}.station_filter`)}
                    >
                      <FilterChip
                        active={station === ALL_STATIONS}
                        onClick={() => setStation(ALL_STATIONS)}
                      >
                        {t(`${I18N_BASE}.all_stations`)}
                      </FilterChip>
                      {stationOptions.map((option) => (
                        <FilterChip
                          key={option.value}
                          active={station === option.value}
                          onClick={() => setStation(option.value)}
                        >
                          {option.label}
                        </FilterChip>
                      ))}
                    </div>
                    <span className="h-6 w-px bg-border" aria-hidden="true" />
                  </>
                ) : null}

                <FilterChip
                  active={onlyDelayed}
                  tone="danger"
                  count={delayedCount}
                  onClick={() => setOnlyDelayed((current) => !current)}
                >
                  {t(`${I18N_BASE}.only_delayed`)}
                </FilterChip>
              </div>

              {/* Narrow layouts show one column at a time; at `lg` every column
                  is on screen and these chips would be a second way to say the
                  same thing. */}
              <div
                className="flex flex-wrap items-center gap-2 lg:hidden"
                role="group"
                aria-label={t(`${I18N_BASE}.status_filter`)}
              >
                {statusColumns.map((column) => (
                  <FilterChip
                    key={column.value}
                    active={visibleStatus === column.value}
                    tone={orderTone(column.value)}
                    count={grouped[column.value]?.length ?? 0}
                    onClick={() => setVisibleStatus(column.value)}
                  >
                    {column.label}
                  </FilterChip>
                ))}
              </div>
            </div>

            {hasVisibleOrders ? (
              <div className="grid gap-x-4 gap-y-6 lg:grid-cols-4">
                {statusColumns.map((column) => (
                  <StatusColumnStack
                    key={column.value}
                    column={column}
                    orders={grouped[column.value] ?? []}
                    now={now}
                    hiddenOnNarrow={column.value !== visibleStatus}
                    advancingOrderId={advancingOrderId}
                    onOpen={setSelectedOrderId}
                    onAdvance={handleAdvance}
                  />
                ))}
              </div>
            ) : (
              <EmptyPanel
                title={t(`${I18N_BASE}.no_matches_title`)}
                hint={t(`${I18N_BASE}.no_matches_hint`)}
              />
            )}
          </>
        ) : (
          <EmptyPanel
            title={t(`${I18N_BASE}.empty_title`)}
            hint={t(`${I18N_BASE}.empty_hint`)}
          />
        )}
      </div>

      <OrderDetailDrawer
        order={selectedOrder}
        open={selectedOrder !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedOrderId(null)
        }}
        cancellationReasons={cancellationReasons}
      />

      <ServiceTypeDrawer open={serviceTypeOpen} onOpenChange={setServiceTypeOpen} />
    </MainContainer>
  )
}

// Where a new order starts. The button used to jump straight to the pickup form,
// which is the rarer of the two: most orders are taken at a table, and a waiter
// who pressed "Nueva comanda" had to back out and go find /tables. Asking first
// costs one tap and sends each job to the screen built for it.
function ServiceTypeDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const go = (href: string) => {
    onOpenChange(false)
    router.visit(href)
  }

  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="orders-service-type-drawer">
        <DrawerHeader>
          <DrawerTitle>{t(`${I18N_BASE}.service_type_title`)}</DrawerTitle>
          <DrawerDescription>{t(`${I18N_BASE}.service_type_description`)}</DrawerDescription>
        </DrawerHeader>

        <div className="space-y-2 px-4 pb-6">
          <ServiceTypeOption
            icon={<Utensils className="size-5" aria-hidden="true" />}
            title={t(`${I18N_BASE}.service_type_dine_in`)}
            hint={t(`${I18N_BASE}.service_type_dine_in_hint`)}
            onClick={() => go("/tables")}
            testId="orders-service-type-dine-in"
          />
          <ServiceTypeOption
            icon={<ShoppingBag className="size-5" aria-hidden="true" />}
            title={t(`${I18N_BASE}.service_type_to_go`)}
            hint={t(`${I18N_BASE}.service_type_to_go_hint`)}
            onClick={() => go("/orders/new")}
            testId="orders-service-type-to-go"
          />
        </div>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}

function ServiceTypeOption({
  icon,
  title,
  hint,
  onClick,
  testId,
}: {
  icon: React.ReactNode
  title: string
  hint: string
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="border-input hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/50 flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left outline-none focus-visible:ring-[3px]"
    >
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="text-muted-foreground block text-xs">{hint}</span>
      </span>
      <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
    </button>
  )
}

function StatusColumnStack({
  column,
  orders,
  now,
  hiddenOnNarrow,
  advancingOrderId,
  onOpen,
  onAdvance,
}: {
  column: StatusColumn
  orders: KanbanOrder[]
  now: Date
  hiddenOnNarrow: boolean
  advancingOrderId: number | null
  onOpen: (id: number) => void
  onAdvance: (order: KanbanOrder, action: OrderFlowAction) => void
}) {
  const tone = orderTone(column.value)

  return (
    <section
      className={cn("space-y-2", hiddenOnNarrow ? "hidden lg:block" : "block")}
      data-testid={`orders-column-${column.value}`}
      aria-label={column.label}
    >
      <div className="flex items-center justify-between gap-2 border-b pb-2">
        <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold">
          <span className={toneDotClass(tone)} aria-hidden="true" />
          <span className="truncate">{column.label}</span>
        </h2>
        <span
          className={cn(
            "shrink-0 text-sm font-medium tabular-nums",
            orders.length === 0 ? "text-muted-foreground/40" : "text-muted-foreground"
          )}
        >
          {orders.length}
        </span>
      </div>

      {orders.length === 0 ? (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          {t(`${I18N_BASE}.column_empty`)}
        </p>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              now={now}
              advancing={advancingOrderId === order.id}
              onOpen={() => onOpen(order.id)}
              onAdvance={onAdvance}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function OrderCard({
  order,
  now,
  advancing,
  onOpen,
  onAdvance,
}: {
  order: KanbanOrder
  now: Date
  advancing: boolean
  onOpen: () => void
  onAdvance: (order: KanbanOrder, action: OrderFlowAction) => void
}) {
  const ageMinutes = orderAgeMinutes(order.placedAt, now)
  const urgency = orderUrgency(order.status, ageMinutes)
  const action = primaryActionFor(order)
  const place = order.tableName
    ? `${t("ui.common.table", { count: 1 })} ${order.tableName}`
    : t("ui.common.no_table")

  return (
    <article
      className={cn(
        "rounded-lg border bg-card shadow-xs transition-colors hover:border-ring/60",
        urgency === "late" && "border-destructive/40"
      )}
      data-testid={`order-card-${order.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-col gap-1.5 rounded-lg p-3 text-left transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <span className="sr-only">{t(`${I18N_BASE}.open_detail`)}</span>

        <span className="flex w-full items-start gap-2">
          <span className="inline-flex min-w-7 shrink-0 items-center justify-center rounded-md bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums">
            <span className="sr-only">{t(`${I18N_BASE}.quantity`)} </span>
            {order.quantity}×
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{order.name}</span>
          <AgeMark ageMinutes={ageMinutes} urgency={urgency} />
        </span>

        <span className="flex w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{place}</span>
          <Separator />
          <span>{order.serviceTypeLabel}</span>
          <Separator />
          <span>{order.stationLabel}</span>
          <Separator />
          <span className="tabular-nums">{t(`${I18N_BASE}.bill`, { id: order.billId })}</span>
        </span>

        {order.pickupEtaMinutes !== null ? (
          <span className="text-xs font-medium tabular-nums">
            {t(`${I18N_BASE}.eta`, { count: order.pickupEtaMinutes })}
          </span>
        ) : null}

        {order.notes ? (
          <span className="w-full rounded-md bg-muted/60 px-2 py-1 text-xs text-foreground">
            {t(`${I18N_BASE}.note`, { note: order.notes })}
          </span>
        ) : null}
      </button>

      {action ? (
        <div className="border-t p-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={advancing}
            onClick={() => onAdvance(order, action)}
          >
            {t(action.labelKey)}
          </Button>
        </div>
      ) : null}
    </article>
  )
}

function AgeMark({ ageMinutes, urgency }: { ageMinutes: number; urgency: Urgency }) {
  const tone = urgency === "late" ? "danger" : urgency === "warn" ? "warning" : null
  const Icon = urgency === "late" ? TriangleAlert : Clock

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 text-xs tabular-nums",
        tone ? cn(toneTextClass(tone), "font-semibold") : "text-muted-foreground"
      )}
    >
      <Icon className="size-3" aria-hidden="true" />
      <span className="sr-only">{t(`${I18N_BASE}.waiting_time`)} </span>
      {ageMinutes < 1
        ? t(`${I18N_BASE}.just_placed`)
        : t(`${I18N_BASE}.minutes_short`, { count: ageMinutes })}
      {/* Colour alone must never be the escalation, so name it for anyone who
          cannot see the hue. */}
      {tone ? <span className="sr-only"> — {t(`${I18N_BASE}.delayed`)}</span> : null}
    </span>
  )
}

function Separator() {
  return <span className="size-0.5 rounded-full bg-muted-foreground/40" aria-hidden="true" />
}

function EmptyPanel({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="rounded-xl border bg-card px-6 py-16 text-center shadow-sm">
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
    </div>
  )
}
